/* eslint-disable jsx-a11y/anchor-is-valid */
import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent, useCallback } from "react";
import {
  Collection,
  ShopPageNav,
  ShopPageNavProps,
  CollectionCard,
  ArticleCardCarousel,
  SuperHero,
} from "@bluebottlecoffee/design-system/components";
import { ParsedUrlQuery } from "querystring";
import {
  ProductVariant,
  useAnalytics,
  useCart,
  useProduct,
  Product,
} from "@chordcommerce/react-autonomy";
import {
  selectHeroes,
  toNarrowSuperHeroProps,
} from "../../../../lib/transformers/super-hero";
import { CanonicalHead } from "../../../../components/CanonicalHead";
import { getPinnedOrder } from "../../../../lib/transformers/shop-card";
import useVariantPrice from "../../../../lib/chord/hooks/components/use-variant-price";
import {
  ShippingCopy,
  ShippingCopy as ShippingCopySchema,
} from "../../../../lib/sanity-schema";
import { shippingCopyQuery } from "../../../../lib/sanity/product-queries";
import { MiniCartDialog } from "../../../../components/MiniCartDialog";
import { isFeatureEnabled } from "../../../../lib/utils/is-feature-enabled";
import renderModule from "../../../../lib/render-module";
import sanityClient from "../../../../lib/sanity-client";
import { useIsClient } from "../../../../lib/hooks/use-is-client";
import {
  debugPrintBuilder,
  getDefaultStaticPropsMiniCartWithSlug,
  getHomepageModules,
  quickShopCarouselAndArticleCardCarousel,
  QuickShopCarouselAndArticleCardCarouselProps,
} from "../../../../lib/utils";
import {
  buildSanityQuery,
  QueryKey,
  SanityQueryType,
  ShopPageResult,
  shopPageSlugsQuery,
} from "../../../../lib/sanity/shop-queries";
import { CartBasePropsWithSlug } from "../../../../lib/sanity/shared";
import { toShopPageNavItems } from "../../../../lib/transformers/shop-page-nav";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import {
  AlgoliaCollectionPreview,
  getCollectionPreview,
} from "../../../../lib/algolia/collection-preview";
import localization from "../../../../localization";
import { REGION } from "../../../../lib/i18n";
import {
  buildTranslationGroupQuery,
  toCopyObject,
  TranslationGroupSanityType,
} from "../../../../lib/sanity/translation-group-queries";
import { ConversionCopy } from "../product/[slug]";
import useVariantAvailability from "../../../../lib/chord/hooks/components/use-variant-availability";
import { buildAbsoluteUrl } from "../../../../lib/link-builders";
import { QuickShopCarouselWrapper } from "../../../../components/QuickShopCarouselWrapper";
import { toArticleCardCarouselProps } from "../../../../lib/transformers/article-card-carousel";
import { toMarketingCardProps } from "../../../../lib/transformers/marketing-card";
import {
  FacetFiltersCopy,
  toFacetFiltersProps,
  toSortProps,
} from "../../../../lib/transformers/facet-filters";
import { trackEvent } from "../../../../lib/segment/track-event";
import { resolveTabbedCollections } from "../../../../lib/resolve-tabbed-collections";

interface PageData extends CartBasePropsWithSlug {
  shopPage: ShopPageResult & {
    collectionPreview: AlgoliaCollectionPreview[];
  };
  facetFiltersCopy: FacetFiltersCopy;
  homePageModules: QuickShopCarouselAndArticleCardCarouselProps;
  shopcardCopy: {
    copy: ConversionCopy;
    shippingCopy: ShippingCopy;
  };
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } =
    await getDefaultStaticPropsMiniCartWithSlug(context);

  const { lang, region, slug } = defaultProps;

  const sanityQueryResult = await sanityClient().fetch<SanityQueryType>(
    buildSanityQuery(slug, lang),
  );

  const shopPage = sanityQueryResult[QueryKey.SHOP_PAGE];

  const shippingCopy =
    await sanityClient().fetch<ShippingCopySchema>(shippingCopyQuery);

  const conversionTranslationGroupQuery = buildTranslationGroupQuery(
    lang,
    "Conversion",
  );
  const collectionTranslationGroupQuery = buildTranslationGroupQuery(
    lang,
    "Collection",
  );

  const [conversionCopy, facetFiltersCopy] = await Promise.all([
    sanityClient()
      .fetch<TranslationGroupSanityType>(conversionTranslationGroupQuery)
      .then((translations) => toCopyObject<ConversionCopy>(lang, translations)),
    sanityClient()
      .fetch<TranslationGroupSanityType>(collectionTranslationGroupQuery)
      .then((translations) =>
        toCopyObject<FacetFiltersCopy>(lang, translations),
      ),
  ]);

  const collectionPreview = await Promise.all(
    shopPage.shopCollections?.map(
      ({ name: collectionName, slug: collectionSlug }) =>
        getCollectionPreview({
          collectionName,
          collectionSlug,
          lang,
          region,
          urlQueryParams: `?nav=${slug},${shopPage.name}`,
          shippingCopy,
          copy: conversionCopy,
        }),
    ),
  );

  const homePageModules = await getHomepageModules<PageData["homePageModules"]>(
    quickShopCarouselAndArticleCardCarousel,
    lang,
    slug,
  );

  await resolveTabbedCollections(shopPage?.modules, { lang, region });

  return {
    props: {
      ...defaultProps,
      facetFiltersCopy,
      shopPage: {
        ...shopPage,
        collectionPreview: collectionPreview ?? [],
      },
      homePageModules,
      shopcardCopy: {
        copy: conversionCopy,
        shippingCopy,
      },
    },
  };
};

export const getStaticPaths: GetStaticPaths<ParsedUrlQuery> = async () => {
  const shopPageSlugs =
    await sanityClient().fetch<string[]>(shopPageSlugsQuery);

  const params = await Promise.all(
    localization.languages(REGION).flatMap((lang) =>
      shopPageSlugs.map(async (slug: string) => ({
        params: {
          region: REGION,
          lang: lang.code,
          slug,
        },
      })),
    ),
  );

  return {
    paths: params,
    fallback: false,
  };
};

const ShopPage: FunctionComponent<PageData> = ({
  shopPage,
  facetFiltersCopy,
  homePageModules,
  shopcardCopy,
  ...layoutWrapperProps
}) => {
  const isClient = useIsClient();
  const {
    region,
    lang,
    slug,
    giftCardFormCopy,
    subscribableProducts,
    cart,
    navAndCartInfoBanner,
    productRecs,
    aria,
  } = layoutWrapperProps;

  const {
    altBBLogo,
    marketingCards,
    metaTags,
    modules,
    title,
    hideCollectionBubbles,
    filtersEnabled,
    collectionPreview: collectionsData,
    pinnedProducts,
  } = shopPage;

  const { createStockRequest } = useProduct();
  const { addToCart: chordAddToCart, addSubscription } = useCart();

  const addToCart = useCallback(
    (sku: string, quantity: number) => chordAddToCart({ sku, quantity }),
    [chordAddToCart],
  );

  const { trackCollectionClicked, trackProductClicked } = useAnalytics();

  const handleCreateStockRequest = async ({ email, sku }, product: Product) => {
    const productData: Product = {
      ...product,
      variant:
        product?.variant?.sku !== sku
          ? ({
              sku,
            } as ProductVariant)
          : product?.variant,
    };

    await createStockRequest({ email, product: productData });
  };

  const collectionPreview = collectionsData.map((previewCollection) => {
    const shopCards = previewCollection.shopCards.map((card) => ({
      ...card,
      subscribeProduct: addSubscription,
      addToCart,
      useVariantAvailability,
      useVariantPrice,
      createStockRequest: (data) =>
        handleCreateStockRequest(data, card.trackShopcardData.product),
      clickTracking: (event, props) => {
        if (event === "plp_quick_add_link") {
          trackProductClicked(card.trackShopcardData);
        }
        trackEvent(event, props);
      },
      product: isFeatureEnabled(process.env.NEXT_PUBLIC_PLP_ATC_ENABLED)
        ? card.product
        : undefined,
    })) as Array<CollectionCard & { productSlug: string }>;

    return {
      ...previewCollection,
      shopCards,
    };
  });

  const shopPageNavItems = toShopPageNavItems({
    region,
    lang,
    navLinks: [shopPage],
    trackClickCollection: (
      display: string,
      collectionSlug: string,
      url: string,
    ) => {
      const absoluteUrl = buildAbsoluteUrl({ path: url });
      trackCollectionClicked({
        collection: {
          collectionId: collectionSlug,
          url: absoluteUrl,
          title: display,
          brand: process.env.NEXT_PUBLIC_CHORD_OMS_BRAND_NAME,
        },
      });
    },
  });

  const shopPageNavProps: ShopPageNavProps[] = shopPageNavItems.map(
    (navItems) => ({
      ...navItems,
      tags: {},
      debugPrint: debugPrintBuilder("ShopPageNav"),
    }),
  );

  const miniCartDialog = (
    <MiniCartDialog
      productRecs={productRecs}
      giftCardFormCopy={giftCardFormCopy}
      miniCartCopy={cart}
      navAndCartInfoBanner={navAndCartInfoBanner}
      region={region}
      lang={lang}
      subscribableProducts={
        isFeatureEnabled(
          process.env.NEXT_PUBLIC_CART_SUBSCRIPTION_TOGGLE_ENABLED,
        )
          ? subscribableProducts
          : []
      }
    />
  );

  const seen = new Set<string>();
  const allShopCards = collectionPreview
    .flatMap(({ shopCards }) => shopCards)
    .filter(({ productSlug }) =>
      seen.has(productSlug) ? false : seen.add(productSlug),
    );

  const filterProps =
    filtersEnabled && shopPage.facetFilters?.length
      ? toFacetFiltersProps(shopPage.facetFilters, facetFiltersCopy)
      : undefined;

  const { hero, heroResponsive } = shopPage;
  const { superHeroProps, heading, description } = toNarrowSuperHeroProps(
    selectHeroes(hero, heroResponsive),
    aria,
    region,
    lang,
  );

  return (
    isClient && (
      <>
        <CanonicalHead region={region} lang={lang} slug={`shop/${slug}`} />
        <LayoutWrapper
          {...{
            ...layoutWrapperProps,
            altBBLogo,
            pageTitle: title ? title[lang] : "Shop | Blue Bottle Coffee",
            metaTags,
            slideOutContent: [miniCartDialog],
            openMiniCartPanel: Boolean(miniCartDialog),
          }}
          isTransparent
        >
          <SuperHero {...superHeroProps} />

          {!hideCollectionBubbles && <ShopPageNav {...shopPageNavProps[0]} />}

          <Collection
            key={slug}
            shopCards={getPinnedOrder({
              products: allShopCards,
              pinnedProducts,
            })}
            marketingCards={marketingCards?.map((card) =>
              toMarketingCardProps({ data: card, lang, region }),
            )}
            initMobileGridCols={2}
            name={heading}
            description={description}
            aria={aria}
            filterMode="union"
            filterProps={filterProps}
            sortProps={toSortProps(facetFiltersCopy)}
            hideCtaOnMobile
          />

          {modules?.length > 0 &&
            modules.map((module) =>
              renderModule({ module, region, lang, aria }),
            )}

          {homePageModules.quickShopCarousel && (
            <QuickShopCarouselWrapper
              shopcardCopy={shopcardCopy}
              moduleProps={homePageModules.quickShopCarousel}
              lang={lang}
              region={region}
            />
          )}

          {homePageModules.articleCardCarousel && (
            <ArticleCardCarousel
              {...toArticleCardCarouselProps(
                homePageModules.articleCardCarousel,
                region,
                lang,
              )}
            />
          )}
        </LayoutWrapper>
      </>
    )
  );
};

export default ShopPage;
